import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Eye } from 'lucide-react';
import { adminApi, eventsApi } from '../../services/api';

const MODULES = ['chat', 'intent_classification', 'document_processing', 'rag_answer', 'rag_embedding', 'guardrail_validation', 'sentiment_analysis', 'conversation_summary', 'fallback_response', 'feedback_translation', 'whatsapp_audio_transcription', 'data_query_build', 'data_query_answer', 'data_schema_generation'];

const emptyForm = () => ({
  module: '',
  scopeType: 'global',
  scopeId: '',
  modelId: '',
  maxOutputTokens: '',
  temperature: '',
  topP: '',
  topK: '',
  isActive: true,
});

const AIModuleMappings: React.FC = () => {
  const [mappings, setMappings] = useState<any[]>([]);
  const [models, setModels] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [viewing, setViewing] = useState<any | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const res = await adminApi.listAIModuleMappings({ page: pagination.page, limit: pagination.limit });
      const payload = res.data?.data as { mappings?: any[]; pagination?: { total?: number } } | undefined;
      setMappings(payload && Array.isArray(payload.mappings) ? payload.mappings : []);
      setPagination(prev => ({ ...prev, total: payload?.pagination?.total ?? 0 }));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const res = await adminApi.listAIModels({ limit: 500 });
      const payload = res.data?.data as { models?: any[] } | undefined;
      setModels(payload && Array.isArray(payload.models) ? payload.models : []);
    } catch (_) {}
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchMappings(); }, [pagination.page, pagination.limit]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchModels(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    eventsApi.getAll({ limit: 500 }).then(r => setEvents(r.data?.data || [])).catch(() => {});
    adminApi.getAllUsers({ role: 'client', limit: 500 }).then(r => setClients(r.data?.data?.users || [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setModalVisible(true);
  };

  const openEdit = (record: any) => {
    setEditing(record);
    const modelId = typeof record.modelId === 'object' ? record.modelId?._id : record.modelId;
    setForm({
      module: record.module || '',
      scopeType: record.scopeType || 'global',
      scopeId: record.scopeId || '',
      modelId: modelId || '',
      maxOutputTokens: record.maxOutputTokens ?? '',
      temperature: record.temperature ?? '',
      topP: record.topP ?? '',
      topK: record.topK ?? '',
      isActive: record.isActive ?? true,
    });
    setModalVisible(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.module || !form.modelId) {
      toast.error('Module and AI model are required');
      return;
    }
    try {
      const payload: any = {
        module: form.module,
        scopeType: form.scopeType,
        modelId: form.modelId,
        isActive: form.isActive,
        ...(form.scopeType !== 'global' && form.scopeId && { scopeId: form.scopeId }),
        ...(form.maxOutputTokens !== '' && { maxOutputTokens: Number(form.maxOutputTokens) }),
        ...(form.temperature !== '' && { temperature: Number(form.temperature) }),
        ...(form.topP !== '' && { topP: Number(form.topP) }),
        ...(form.topK !== '' && { topK: Number(form.topK) }),
      };
      if (editing) {
        await adminApi.updateAIModuleMapping(editing._id, payload);
        toast.success('Mapping updated');
      } else {
        await adminApi.createAIModuleMapping(payload);
        toast.success('Mapping created');
      }
      setModalVisible(false);
      fetchMappings();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this mapping?')) return;
    try {
      await adminApi.deleteAIModuleMapping(id);
      toast.success('Mapping deleted');
      fetchMappings();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Delete failed');
    }
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  const getModelLabel = (record: any) => {
    const m = record.modelId;
    return m ? (typeof m === 'object' ? m.displayName || m.apiModelName : m) : '–';
  };

  return (
    <div className="p-6 space-y-4">
      {/* Actions bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> Add model-module mapping
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Module', 'Scope', 'Model', 'Active', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
              ) : mappings.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No mappings found</td></tr>
              ) : mappings.map(m => (
                <tr key={m._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{m.module}</td>
                  <td className="px-4 py-3 text-gray-600">{m.scopeType}</td>
                  <td className="px-4 py-3 text-gray-700">{getModelLabel(m)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${m.isActive ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                      {m.isActive ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setViewing(m)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" title="View details"><Eye className="w-3.5 h-3.5" /></button>
                      <button onClick={() => openEdit(m)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(m._id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">Total: {pagination.total}</p>
            <div className="flex items-center gap-2">
              <button disabled={pagination.page <= 1 || loading} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40">Prev</button>
              <span className="text-xs text-gray-600">Page {pagination.page} of {totalPages}</span>
              <button disabled={pagination.page >= totalPages || loading} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">{editing ? 'Edit mapping' : 'Add mapping'}</h2>
              <button onClick={() => setModalVisible(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Module <span className="text-red-500">*</span></label>
                <select value={form.module} onChange={e => setForm(f => ({ ...f, module: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Select module</option>
                  {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scope <span className="text-red-500">*</span></label>
                <select value={form.scopeType} onChange={e => setForm(f => ({ ...f, scopeType: e.target.value, scopeId: '' }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="global">global</option>
                  <option value="client">client</option>
                  <option value="event">event</option>
                </select>
              </div>
              {form.scopeType !== 'global' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {form.scopeType === 'client' ? 'Client' : 'Event'} <span className="text-red-500">*</span>
                  </label>
                  <select value={form.scopeId} onChange={e => setForm(f => ({ ...f, scopeId: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select {form.scopeType === 'client' ? 'client' : 'event'}</option>
                    {form.scopeType === 'client'
                      ? clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)
                      : events.map(ev => <option key={ev._id} value={ev._id}>{ev.name || ev.eventId}</option>)
                    }
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">AI model <span className="text-red-500">*</span></label>
                <select value={form.modelId} onChange={e => setForm(f => ({ ...f, modelId: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Select model</option>
                  {models.map(m => <option key={m._id} value={m._id}>{m.displayName} ({m.provider})</option>)}
                </select>
              </div>
              {[
                { key: 'maxOutputTokens', label: 'Max output tokens (override)', step: '1', min: '1' },
                { key: 'temperature', label: 'Temperature (override)', step: '0.1', min: '0', max: '2' },
                { key: 'topP', label: 'Top P (override)', step: '0.1', min: '0', max: '1' },
                { key: 'topK', label: 'Top K (override)', step: '1', min: '1' },
              ].map(({ key, label, step, min, max }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input type="number" step={step} min={min} max={max} value={(form as any)[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              ))}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Active</label>
                <button type="button" onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isActive ? 'bg-blue-600' : 'bg-gray-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setModalVisible(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View details modal */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Mapping Details</h2>
              <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div><span className="text-gray-500">Module:</span> <span className="font-mono text-gray-900">{viewing.module || '–'}</span></div>
              <div><span className="text-gray-500">Scope:</span> <span className="text-gray-900">{viewing.scopeType || '–'}</span></div>
              <div><span className="text-gray-500">Scope ID:</span> <span className="font-mono text-gray-900">{viewing.scopeId || '–'}</span></div>
              <div><span className="text-gray-500">Model:</span> <span className="text-gray-900">{getModelLabel(viewing)}</span></div>
              <div><span className="text-gray-500">Max output tokens:</span> <span className="text-gray-900">{viewing.maxOutputTokens ?? '–'}</span></div>
              <div><span className="text-gray-500">Temperature:</span> <span className="text-gray-900">{viewing.temperature ?? '–'}</span></div>
              <div><span className="text-gray-500">Top P:</span> <span className="text-gray-900">{viewing.topP ?? '–'}</span></div>
              <div><span className="text-gray-500">Top K:</span> <span className="text-gray-900">{viewing.topK ?? '–'}</span></div>
              <div><span className="text-gray-500">Status:</span> <span className="text-gray-900">{viewing.isActive ? 'Active' : 'Inactive'}</span></div>
            </div>
            <div className="flex justify-end gap-2 p-5 pt-0">
              <button onClick={() => setViewing(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Close</button>
              <button
                onClick={() => { const next = viewing; setViewing(null); openEdit(next); }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIModuleMappings;
